import { computed, ComputedRef, ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useAuth } from '@/auth/useAuth';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { getGrowthPopupVariant } from '@/utils/growthPopupExperiment.utils';
import { dismissGrowthPopup, fetchGrowthCta } from '@/api/resources/growth.api';
import { Components } from '@/api/generated/api';
import { growthCtaKeys, invalidateGrowthCtaQueries } from './growthCtaQueryInvalidation';

const refreshSignal = ref(0);

interface IGrowthCtaComposable {
  canShowGrowthPopups: ComputedRef<boolean>;
  popup: ComputedRef<Components.Schemas.GrowthPopupType>;
  isPending: ComputedRef<boolean>;
  refreshSignal: ComputedRef<number>;
  refresh: () => Promise<void>;
  refreshSilently: () => Promise<void>;
  dismissPopup: (popup: Components.Schemas.GrowthPopupType) => Promise<void>;
}

export function useGrowthCta(): IGrowthCtaComposable {
  const { isLoggedIn } = useAuth();
  const { currentTenant } = useCurrentTenant();
  const { whoAmI } = useWhoAmI();
  const queryClient = useQueryClient();

  const queryKey = computed(() => growthCtaKeys.tenant(currentTenant.value?.id));

  const query = useQueryWithToast({
    queryKey,
    queryFn: fetchGrowthCta,
    enabled: computed(() => isLoggedIn.value && !!currentTenant.value),
    meta: { showErrorToast: false },
  });

  const popup = computed<Components.Schemas.GrowthPopupType>(() => query.data.value ?? 'NONE');
  const canShowGrowthPopups = computed(
    () => !!whoAmI.value && getGrowthPopupVariant(whoAmI.value) !== 'checklist_only',
  );
  const isPending = computed(() => query.isPending.value);
  const sharedRefreshSignal = computed(() => refreshSignal.value);

  const invalidate = async (): Promise<void> => {
    await invalidateGrowthCtaQueries(queryClient);
  };

  const refresh = async (): Promise<void> => {
    await invalidate();
    refreshSignal.value += 1;
  };

  const refreshSilently = async (): Promise<void> => {
    await invalidate();
  };

  const dismissPopup = async (popupType: Components.Schemas.GrowthPopupType): Promise<void> => {
    await dismissGrowthPopup(popupType);
    await refreshSilently();
  };
  return {
    canShowGrowthPopups,
    popup,
    isPending,
    refreshSignal: sharedRefreshSignal,
    refresh,
    refreshSilently,
    dismissPopup,
  };
}
